import SessionConfig from '../../model/SessionConfig'
import { logError } from '../../util/errorLogger'
import { authService, edgeFunctionService } from '../domain/_service.config'
import PaymentService, { PassType, TeamData } from '../domain/PaymentService'

export default class StripePaymentService implements PaymentService {
  async createCheckoutSession(
    passType: PassType,
    config: SessionConfig,
    successUrl: string,
    cancelUrl: string,
    challengeId: string,
    teamData?: TeamData,
    teamId?: string,
    actionType?: string,
  ): Promise<string> {
    try {
      const authID = await authService.getCurrentUserAuthID()
      if (!authID) {
        throw new Error('User must be authenticated to create checkout session')
      }
      if (!challengeId) {
        throw new Error('challengeId is required to create checkout session')
      }

      const priceId = this.getPriceId(passType, config)

      const fulfillmentType =
        passType === PassType.INDIVIDUAL
          ? 'user'
          : passType === PassType.LEADER
            ? 'leader'
            : 'team'

      if ((fulfillmentType === 'team' || fulfillmentType === 'leader') && !teamData) {
        throw new Error('teamData is required for team and leader passes')
      }

      const body = {
        priceId,
        successUrl,
        cancelUrl,
        fulfillmentType,
        userId: authID,
        teamId: fulfillmentType === 'user' && teamId ? teamId : null,
        teamData:
          fulfillmentType === 'team' || fulfillmentType === 'leader' ? teamData : undefined,
        actionType: actionType || undefined,
        challenge_id: challengeId,
      }

      const { data, error } = await edgeFunctionService.invoke<{ url?: string }>(
        'create-checkout-session',
        body,
      )

      if (error) throw new Error(`Failed to create checkout session: ${error.message}`)
      if (!data?.url) throw new Error('No checkout session URL returned')

      return data.url
    } catch (error: any) {
      void logError(error, {
        service: 'StripePaymentService',
        action: 'createCheckoutSession',
        passType,
      })
      throw new Error(error.message || 'Failed to create checkout session')
    }
  }

  private getPriceId(passType: PassType, config: SessionConfig): string {
    let priceId: string | undefined

    switch (passType) {
      case PassType.INDIVIDUAL:
        priceId = config.passPriceId
        break
      case PassType.TEAM:
        priceId = config.teamPassPriceId
        break
      case PassType.LEADER:
        priceId = config.teamLeaderPassPriceId
        break
      default:
        throw new Error(`Invalid pass type: ${passType}`)
    }

    if (!priceId) {
      throw new Error(
        `Missing price ID for pass type: ${passType}. Check challenge pricing configuration.`,
      )
    }

    return priceId
  }
}
